import type { PluginManifest } from '../types';
import { OPENAI_MODELS, ANTHROPIC_MODELS } from '../lib/api';

/**
 * Built-in plugin manifests. Instances are user-created
 * (BYOK: keys stored locally in the zustand-persisted store).
 */
export const BUILTIN_PLUGINS: PluginManifest[] = [
  {
    id: 'builtin-openai',
    name: 'OpenAI',
    version: '1.0.0',
    description: 'GPT-4o, GPT-4 Turbo, o1-mini & more via your own API key. Chat + streaming.',
    author: 'AI Dashboard',
    category: 'llm',
    capabilities: ['chat', 'stream', 'function_call', 'vision'],
    icon: 'Sparkles',
    entryPoint: 'OpenAIChat',
    permissions: ['network:api.openai.com'],
    tags: ['gpt', 'openai', 'llm', 'chat'],
    homepage: 'https://platform.openai.com',
    license: 'BYOK',
    configFields: [
      { key: 'apiKey', label: 'OpenAI API Key', type: 'password', required: true, placeholder: 'sk-...', description: 'Stored locally in your browser only.' },
      {
        key: 'model', label: 'Model', type: 'select', default: 'gpt-4o-mini',
        options: OPENAI_MODELS.map((m) => ({ value: m, label: m })),
      },
      { key: 'temperature', label: 'Temperature', type: 'number', default: 0.7 },
      { key: 'systemPrompt', label: 'System Prompt', type: 'string', default: 'You are a helpful assistant.', placeholder: 'You are a helpful assistant.' },
    ],
  },
  {
    id: 'builtin-anthropic',
    name: 'Anthropic Claude',
    version: '1.0.0',
    description: 'Claude Sonnet, Opus & Haiku via your own API key. Chat + streaming.',
    author: 'AI Dashboard',
    category: 'llm',
    capabilities: ['chat', 'stream', 'vision'],
    icon: 'Brain',
    entryPoint: 'AnthropicChat',
    permissions: ['network:api.anthropic.com'],
    tags: ['claude', 'anthropic', 'llm', 'chat'],
    homepage: 'https://console.anthropic.com',
    license: 'BYOK',
    configFields: [
      { key: 'apiKey', label: 'Anthropic API Key', type: 'password', required: true, placeholder: 'sk-ant-...', description: 'Stored locally in your browser only.' },
      {
        key: 'model', label: 'Model', type: 'select', default: 'claude-3-5-haiku-20241022',
        options: ANTHROPIC_MODELS.map((m) => ({ value: m, label: m })),
      },
      { key: 'temperature', label: 'Temperature', type: 'number', default: 0.7 },
      { key: 'systemPrompt', label: 'System Prompt', type: 'string', default: 'You are a helpful assistant.', placeholder: 'You are a helpful assistant.' },
    ],
  },
  {
    id: 'builtin-agent',
    name: 'Chain Agent',
    version: '1.0.0',
    description: 'Multi-step agent: splits a task into plan → execute → review using any configured LLM instance.',
    author: 'AI Dashboard',
    category: 'agent',
    capabilities: ['chat', 'stream'],
    icon: 'Bot',
    entryPoint: 'ChainAgent',
    permissions: ['plugin:call'],
    tags: ['agent', 'chain', 'automation'],
    license: 'MIT',
    configFields: [
      { key: 'llmInstanceId', label: 'LLM Instance ID', type: 'string', required: true, description: 'ID of an OpenAI/Anthropic instance to run steps on.' },
      { key: 'maxSteps', label: 'Max Steps', type: 'number', default: 5 },
    ],
  },
  {
    id: 'builtin-python',
    name: 'JavaScript Runner',
    version: '1.0.0',
    description: 'Run JavaScript/TypeScript snippets safely in a sandboxed worker with captured console output.',
    author: 'AI Dashboard',
    category: 'tool',
    capabilities: ['code_exec'],
    icon: 'Terminal',
    entryPoint: 'CodeRunner',
    permissions: [],
    tags: ['code', 'javascript', 'sandbox'],
    license: 'MIT',
    configFields: [],
  },
  {
    id: 'builtin-rest',
    name: 'REST API Caller',
    version: '1.0.0',
    description: 'Call any REST endpoint with custom method, headers and body. Save presets as instances.',
    author: 'AI Dashboard',
    category: 'api',
    capabilities: ['api_call'],
    icon: 'Globe',
    entryPoint: 'RestCaller',
    permissions: ['network:*'],
    tags: ['rest', 'http', 'api'],
    license: 'MIT',
    configFields: [
      { key: 'baseUrl', label: 'Base URL', type: 'string', required: true, placeholder: 'https://api.example.com' },
      { key: 'defaultHeaders', label: 'Default Headers (JSON)', type: 'json', default: '{}' },
    ],
  },
  {
    id: 'builtin-html',
    name: 'Custom HTML App',
    version: '1.0.0',
    description: 'Paste your own HTML/CSS/JS and run it sandboxed in an iframe. Perfect for widgets, landing pages & prototypes.',
    author: 'AI Dashboard',
    category: 'custom',
    capabilities: [],
    icon: 'Code2',
    entryPoint: 'HtmlApp',
    permissions: [],
    tags: ['html', 'custom', 'widget', 'embed'],
    license: 'MIT',
    configFields: [
      { key: 'html', label: 'HTML Source', type: 'code', required: true, placeholder: '<h1>Hello</h1>', description: 'Full HTML document or snippet. Rendered in a sandboxed iframe.' },
      { key: 'height', label: 'Height (px)', type: 'number', default: 400 },
    ],
  },
  {
    id: 'builtin-webhook',
    name: 'Webhook Receiver',
    version: '1.0.0',
    description: 'Generate a payload template and inspect outgoing webhook calls. Pair with external automation (n8n, Zapier).',
    author: 'AI Dashboard',
    category: 'workflow',
    capabilities: ['api_call'],
    icon: 'Webhook',
    entryPoint: 'Webhook',
    permissions: ['network:*'],
    tags: ['webhook', 'automation', 'n8n', 'zapier'],
    license: 'MIT',
    configFields: [
      { key: 'targetUrl', label: 'Target URL', type: 'string', required: true, placeholder: 'https://...' },
      { key: 'secret', label: 'Secret (optional)', type: 'password' },
    ],
  },
];
